import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useRecoilState } from 'recoil';
import ErrorMessage from 'src/app/components/ErrorMessage/ErrorMessage';
import ScrollButton from 'src/app/components/ScrollButton/ScrollButton';
import Spinner from 'src/app/components/Spinner/Spinner';
import TutorChatBubble from 'src/app/components/TutorChatBubble/TutorChatBubble';
import UserChatBubble from 'src/app/components/UserChatBubble/UserChatBubble';
import { testIds } from 'src/app/constants';
import useFeedback from 'src/app/hooks/network/useFeedback';
import useSendConversation, { TUserInputType } from 'src/app/hooks/network/useSendConversation';
import { conversationsAtom, frameParamsAtom, tutorStateAtom } from 'src/app/state/atoms';
import styles from './Conversations.module.scss';
import captureEvent from 'src/app/lib/gtm-functions';
import useTelemetryLoaded from 'src/app/hooks/telemetry/useTelemetryLoaded';
import { pageviewEvent } from 'src/app/lib/gtm-telemetry';
import { isTelemetryEnabled } from 'src/app/utils/getTelemetrySwitch';
import { IConversationMessage } from 'src/types/serviceResponseTypes';
import { MarkFeedbackType, TutorStateAtomType } from 'src/types/generic';
// import { useActiveStepObserver } from 'src/app/components/Stepper/useActiveStepObserver';

/**
 * Conversation view containing tutor and user chat bubble
 */
function Conversations() {
  const containerRef = useRef<HTMLSpanElement>(null);
  const postConversation = useSendConversation();
  const [markFeedback] = useFeedback();

  const [conversations] = useRecoilState(conversationsAtom);
  const [tutorState] = useRecoilState(tutorStateAtom);
  const [frameParams] = useRecoilState(frameParamsAtom);
  // const [, setActiveStep] = useRecoilState(activeChatActionState);

  // const [messageLength, setMessageLength] = useState(2);
  const [eventLogged, setEventLogged] = useState(false);
  // Track which answered message released the gate (not a boolean — avoids stale
  // true on Q2+ where the next question would announce before useEffect resets).
  const [releasedGateMessageId, setReleasedGateMessageId] = useState<
    IConversationMessage['id'] | null
  >(null);

  useTelemetryLoaded();
  // useActiveStepObserver(setActiveStep, containerRef  as RefObject<HTMLElement>);

  const messages = conversations?.data?.messages;
  const feedbackGateMessage = useMemo(() => {
    if (!messages?.length || tutorState.isHistoryMode) {
      return null;
    }

    // On a retry the answered question is updated in place and stays last,
    // carrying the explanation — nothing to hold back in that case.
    // APAI-2402: only a next question is held back. A free-text tutor reply
    // (e.g. the answer to "Explain") must be announced as it arrives.
    const last = messages[messages.length - 1];
    if (!last || last.from_user || last.explanation || last.content_type === 'single_input') {
      return null;
    }

    const answered = [...messages.slice(0, -1)]
      .reverse()
      .find((message) => !message.from_user && message.explanation);
    if (!answered) {
      return null;
    }

    const isCorrectAnswer =
      answered.is_correct === true ||
      answered.is_correct === 'full' ||
      answered.is_correct === 'partial';

    // Gate when the flow moves on: correct answer, or final attempt.
    if (isCorrectAnswer || answered.attempts_left === 0) {
      return answered;
    }

    return null;
  }, [messages, tutorState.isHistoryMode]);

  const shouldGateNextQuestion =
    Boolean(feedbackGateMessage) && releasedGateMessageId !== feedbackGateMessage?.id;

  const releaseNextQuestionGate = useCallback(() => {
    if (feedbackGateMessage?.id != null) {
      setReleasedGateMessageId(feedbackGateMessage.id);
    }
  }, [feedbackGateMessage?.id]);

  useEffect(() => {
    if (
      frameParams &&
      frameParams.book_code &&
      conversations &&
      conversations.data.id &&
      !eventLogged
    ) {
      // TODO: remove the log, when implementation is success
      const { partner, course_id } = frameParams;
      // console.log('_onFrameParameters', '_frameParameters', frameParams);

      if (isTelemetryEnabled()) {
        // PageViewEvents for Conversation screen
        pageviewEvent(
          'conversation',
          partner ?? 'Mastering',
          `${conversations.data.id}`,
          new Date().valueOf().toString(),
          partner ?? 'Mastering',
          'courseWare',
          course_id ?? 'null',
          'null', // course_section_id
          frameParams.book_code || 'null'
        );

        // Telemetry: Start AI Conversation
        captureEvent({
          event_action: 'Start AI Conversation',
          intention: 'Office Hours',
          chat_type: 'Practise',
          conversation_id: conversations.data.id,
          mlm_book_code: frameParams?.book_code ?? 'N/A',
          app_id: 'MLMAITutor',
          exercise_id: frameParams?.exercise_id,
          user_role: frameParams?.['user-role'],
          person_id_type: frameParams?.person_id_type,
          login_session_id: frameParams?.login_session_id,
        });
      }
      setEventLogged(true);
    }
  }, [conversations, frameParams, eventLogged]);

  return (
    <>
      {/* APAI-2402: aria-live is fixed at 'polite' and never toggled. Turning a
          live region off and back on makes screen readers re-announce the whole
          region, which read the log again from the intro message. Ordering the
          result before the next question is the explanation's own assertive
          region's job (Explanation), not the log's. */}
      <section
        id={testIds.conversationSection}
        className={`${styles.conversationContainer} ${
          tutorState.isHistoryMode ? styles.noFooter : ''
        }`}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-atomic="false"
      >
        {conversations.error && <ErrorMessage errorMessage={conversations.error} />}
        {messages?.map((message, index) =>
          <ConversationItem 
            key={message.id} 
            index={index} 
            message={message}
            postConversation={postConversation}
            markFeedback={markFeedback}
            tutorState={tutorState}
            isLastItem={index === messages.length - 1}
            isSecondLastItem={
              index === messages.length - 2 || message.id === feedbackGateMessage?.id
            }
            onReleaseNextQuestionGate={
              message.id === feedbackGateMessage?.id ? releaseNextQuestionGate : undefined
            }
            shouldFocusExplanation={
              shouldGateNextQuestion && message.id === feedbackGateMessage?.id
            }
          />
        )}
        {conversations.loading && <Spinner />}
        <ScrollButton conversations={conversations.data} />
      </section>
    </>
  );
}

export default Conversations;

function ConversationItem(props: { 
    message: IConversationMessage, 
    index: number, 
    postConversation: (optionKey: string, userInputType?: TUserInputType) => void,
    markFeedback: (options: MarkFeedbackType) => void,
    tutorState: TutorStateAtomType;
    isLastItem?: boolean
    isSecondLastItem?: boolean
    hideFromScreenReader?: boolean
    onReleaseNextQuestionGate?: () => void
    shouldFocusExplanation?: boolean
  }){
  const {
    message,
    index,
    postConversation,
    markFeedback,
    tutorState,
    isLastItem,
    isSecondLastItem,
    hideFromScreenReader,
    onReleaseNextQuestionGate,
    shouldFocusExplanation,
  } = props;

  if(message.from_user){
    return (
      <UserChatBubble conversation={message} hideFromScreenReader={hideFromScreenReader} />
    );
  } else {
    return (
      <TutorChatBubble
        conversation={message}
        isIntroMessage={index === 0}
        onOptionSelect={postConversation}
        markFeedback={markFeedback}
        tutorState={tutorState}
        isLastItem={isLastItem}
        isSecondLastItem={isSecondLastItem}
        onReleaseNextQuestionGate={onReleaseNextQuestionGate}
        hideFromScreenReader={hideFromScreenReader}
        shouldFocusExplanation={shouldFocusExplanation}
      />
    );
  }
}
